// 齊文樓層開放公告 — 後台覆寫（文字或時段）
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { getAdminToken } from '../components/AdminAuthGate';

axios.defaults.withCredentials = true;

const pad2 = (n) => String(n).padStart(2, '0');

function todayYmd() {
  const d = new Date();
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

function addDaysYmd(ymd, delta) {
  const m = String(ymd || '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return todayYmd();
  const dt = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]) + delta, 12, 0, 0, 0);
  return `${dt.getFullYear()}-${pad2(dt.getMonth() + 1)}-${pad2(dt.getDate())}`;
}

function authHeaders() {
  const token = getAdminToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

const ZONES = [
  { key: '1f', label: '1F' },
  { key: '2f_focus', label: '2F 專注區' },
  { key: '2f_open', label: '2F 開放區' },
  { key: '3f', label: '3F' },
];

function segmentsToScheduleRows(segments) {
  if (!Array.isArray(segments)) return [];
  return segments.map((s) => ({
    state: s.state === 'closed' ? 'closed' : 'open',
    startHm: String(s.startHm || '').slice(0, 5),
    endHm: String(s.endHm || '').slice(0, 5),
  }));
}

export default function FloorOpenAdmin() {
  const [date, setDate] = useState(todayYmd);
  const [zone, setZone] = useState('1f');
  const [linesText, setLinesText] = useState('');
  const [scheduleRows, setScheduleRows] = useState([]);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [lastSave, setLastSave] = useState(null);
  const [narrow, setNarrow] = useState(() => typeof window !== 'undefined' && window.innerWidth < 768);

  useEffect(() => {
    const onR = () => setNarrow(window.innerWidth < 768);
    window.addEventListener('resize', onR);
    return () => window.removeEventListener('resize', onR);
  }, []);

  const maxPreview = useMemo(() => addDaysYmd(todayYmd(), 400), []);
  const minPreview = useMemo(() => addDaysYmd(todayYmd(), -400), []);

  const loadPreview = useCallback(async () => {
    setMsg('');
    setLoading(true);
    try {
      const { data } = await axios.get('/api/floor-open/admin/preview', {
        params: { date },
        headers: authHeaders(),
      });
      if (!data?.success) throw new Error(data?.error || 'preview_failed');
      setPreview(data);
    } catch (e) {
      setPreview(null);
      setMsg(e?.response?.data?.message || e?.response?.data?.error || e?.message || '載入失敗');
    } finally {
      setLoading(false);
    }
  }, [date]);

  useEffect(() => {
    loadPreview();
  }, [loadPreview]);

  useEffect(() => {
    if (!preview?.zones) return;
    const row = preview.zones.find((z) => z.zone_key === zone);
    if (!row) return;
    const ov = row.override;
    if (ov && ov.mode === 'schedule' && Array.isArray(ov.segments) && ov.segments.length) {
      setScheduleRows(segmentsToScheduleRows(ov.segments));
      setLinesText('');
    } else {
      setScheduleRows([]);
      const ovLines = ov?.lines;
      setLinesText(Array.isArray(ovLines) && ovLines.length ? ovLines.join('\n') : '');
    }
  }, [preview, zone]);

  const currentZonePreview = preview?.zones?.find((z) => z.zone_key === zone);

  const fillScheduleFromComputed = () => {
    const segs = currentZonePreview?.computed?.segments;
    setScheduleRows(segmentsToScheduleRows(segs));
  };

  const addScheduleRow = () => {
    setScheduleRows((rows) => [...rows, { state: 'open', startHm: '12:00', endHm: '13:00' }]);
  };

  const updateScheduleRow = (idx, field, value) => {
    setScheduleRows((rows) => rows.map((r, i) => (i === idx ? { ...r, [field]: value } : r)));
  };

  const removeScheduleRow = (idx) => {
    setScheduleRows((rows) => rows.filter((_, i) => i !== idx));
  };

  const saveText = async () => {
    setSaving(true);
    setMsg('');
    try {
      const lines = linesText.split(/\r?\n/).map((s) => s.trim()).filter(Boolean);
      const { data } = await axios.put(
        '/api/floor-open/admin/override',
        { date, zone_key: zone, lines },
        { headers: { ...authHeaders(), 'Content-Type': 'application/json' } },
      );
      if (!data?.success) throw new Error(data?.error || 'save_failed');
      setLastSave(data);
      setMsg('已儲存文字覆寫');
      await loadPreview();
    } catch (e) {
      setMsg(e?.response?.data?.error || e?.response?.data?.message || e?.message || '儲存失敗');
    } finally {
      setSaving(false);
    }
  };

  const saveSchedule = async () => {
    setSaving(true);
    setMsg('');
    try {
      const { data } = await axios.put(
        '/api/floor-open/admin/override',
        { date, zone_key: zone, schedule: { segments: scheduleRows } },
        { headers: { ...authHeaders(), 'Content-Type': 'application/json' } },
      );
      if (!data?.success) throw new Error(data?.error || 'save_failed');
      setLastSave(data);
      setMsg('已儲存時段覆寫');
      await loadPreview();
    } catch (e) {
      setMsg(e?.response?.data?.error || e?.response?.data?.message || e?.message || '儲存失敗');
    } finally {
      setSaving(false);
    }
  };

  const clearOverride = async () => {
    if (!window.confirm('確定清除此日此區的覆寫？')) return;
    setSaving(true);
    setMsg('');
    try {
      await axios.delete('/api/floor-open/admin/override', {
        params: { date, zone_key: zone },
        headers: authHeaders(),
      });
      setLinesText('');
      setScheduleRows([]);
      setLastSave(null);
      setMsg('已清除覆寫');
      await loadPreview();
    } catch (e) {
      setMsg(e?.response?.data?.message || e?.response?.data?.error || e?.message || '刪除失敗');
    } finally {
      setSaving(false);
    }
  };

  const displayRight = currentZonePreview?.displaySummaryLines?.join('、') || '—';

  return (
    <div style={{ padding: '12px 8px 32px', maxWidth: 980, margin: '0 auto', color: '#2e2218' }}>
      <h2 style={{ margin: '0 0 16px', fontSize: 22, fontWeight: 800 }}>樓層開放公告覆寫</h2>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-end', marginBottom: 16 }}>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13, fontWeight: 700 }}>
          日期
          <input
            type="date"
            value={date}
            min={minPreview}
            max={maxPreview}
            onChange={(e) => setDate(e.target.value)}
            style={{ padding: '8px 10px', fontSize: 15, borderRadius: 8, border: '1px solid #ccc' }}
          />
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13, fontWeight: 700 }}>
          樓層區塊
          <select
            value={zone}
            onChange={(e) => setZone(e.target.value)}
            style={{ padding: '8px 10px', fontSize: 15, borderRadius: 8, border: '1px solid #ccc', minWidth: 180 }}
          >
            {ZONES.map((z) => (
              <option key={z.key} value={z.key}>{z.label}</option>
            ))}
          </select>
        </label>
        <button
          type="button"
          onClick={loadPreview}
          disabled={loading}
          style={{
            padding: '10px 16px',
            fontWeight: 700,
            borderRadius: 8,
            border: '1px solid #7b4f2f',
            background: '#fff',
            color: '#7b4f2f',
            cursor: loading ? 'wait' : 'pointer',
          }}
        >
          重新載入
        </button>
      </div>

      {msg && (
        <div style={{ marginBottom: 12, fontSize: 14, color: msg.includes('失敗') ? '#b91c1c' : '#15803d' }}>{msg}</div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: narrow ? '1fr' : '1fr 1fr', gap: 16, marginBottom: 18, minWidth: 0 }}>
        <section
          style={{
            border: '1px solid #e8dccb',
            borderRadius: 12,
            padding: 14,
            background: '#fffaf5',
          }}
        >
          <h3 style={{ margin: '0 0 10px', fontSize: 15, fontWeight: 800 }}>系統推算</h3>
          {loading && <p style={{ margin: 0, color: '#666' }}>載入中…</p>}
          {!loading && currentZonePreview && (
            <>
              <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 10 }}>
                {currentZonePreview.computed.summaryLines.join('、')}
              </div>
              {currentZonePreview.computed.activities?.length > 0 && (
                <div style={{ fontSize: 13, marginBottom: 10 }}>
                  <strong>活動</strong>
                  <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
                    {currentZonePreview.computed.activities.map((a, i) => (
                      <li key={i}>{a.time} {a.title}</li>
                    ))}
                  </ul>
                </div>
              )}
              {currentZonePreview.computed.matchedVenues?.length > 0 && (
                <div style={{ fontSize: 12, color: '#555' }}>
                  包場 VENUE：{currentZonePreview.computed.matchedVenues.map((v) => v.VENUE).filter(Boolean).join('、')}
                </div>
              )}
            </>
          )}
        </section>

        <section
          style={{
            border: '1px solid #cfe8ff',
            borderRadius: 12,
            padding: 14,
            background: '#f4f9ff',
          }}
        >
          <h3 style={{ margin: '0 0 10px', fontSize: 15, fontWeight: 800 }}>覆寫後（對外）</h3>
          {!loading && currentZonePreview && (
            <div style={{ fontSize: 14, fontWeight: 600 }}>{displayRight}</div>
          )}
        </section>
      </div>

      <section style={{ marginBottom: 20, padding: 14, border: '1px solid #ddd', borderRadius: 12, background: '#fafafa' }}>
        <h3 style={{ margin: '0 0 10px', fontSize: 15, fontWeight: 800 }}>時段覆寫</h3>
        <p style={{ margin: '0 0 10px', fontSize: 12, color: '#666' }}>
          須自 09:00 連續接到營業窗結束（平日翌日 01:00；週日 19:00），無斷層。狀態：open / closed。
        </p>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
          <button type="button" onClick={fillScheduleFromComputed} disabled={loading || saving} style={btnSm}>
            帶入目前推算
          </button>
          <button type="button" onClick={addScheduleRow} disabled={saving} style={btnSm}>
            新增一段
          </button>
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
          <thead>
            <tr style={{ background: '#eee' }}>
              <th style={th}>狀態</th>
              <th style={th}>開始 HH:mm</th>
              <th style={th}>結束 HH:mm</th>
              <th style={th} />
            </tr>
          </thead>
          <tbody>
            {scheduleRows.map((row, idx) => (
              <tr key={idx}>
                <td style={td}>
                  <select
                    value={row.state}
                    onChange={(e) => updateScheduleRow(idx, 'state', e.target.value)}
                    style={{ width: '100%', padding: 6 }}
                  >
                    <option value="open">open</option>
                    <option value="closed">closed</option>
                  </select>
                </td>
                <td style={td}>
                  <input
                    value={row.startHm}
                    onChange={(e) => updateScheduleRow(idx, 'startHm', e.target.value)}
                    style={{ width: '100%', padding: 6 }}
                    placeholder="09:00"
                  />
                </td>
                <td style={td}>
                  <input
                    value={row.endHm}
                    onChange={(e) => updateScheduleRow(idx, 'endHm', e.target.value)}
                    style={{ width: '100%', padding: 6 }}
                    placeholder="01:00"
                  />
                </td>
                <td style={td}>
                  <button type="button" onClick={() => removeScheduleRow(idx)} style={btnSm}>刪</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div style={{ marginTop: 10 }}>
          <button
            type="button"
            onClick={saveSchedule}
            disabled={saving || !scheduleRows.length}
            style={{ ...btnPrimary, opacity: scheduleRows.length ? 1 : 0.5 }}
          >
            {saving ? '儲存中…' : '儲存時段覆寫'}
          </button>
        </div>
      </section>

      <label style={{ display: 'block', fontSize: 13, fontWeight: 700, marginBottom: 6 }}>文字覆寫（一行一則）</label>
      <textarea
        value={linesText}
        onChange={(e) => setLinesText(e.target.value)}
        rows={5}
        placeholder="選用"
        style={{
          width: '100%',
          maxWidth: '100%',
          boxSizing: 'border-box',
          padding: 12,
          fontSize: 14,
          borderRadius: 10,
          border: '1px solid #ccc',
          marginBottom: 12,
        }}
      />

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        <button type="button" onClick={saveText} disabled={saving} style={btnPrimary}>
          {saving ? '儲存中…' : '儲存文字覆寫'}
        </button>
        <button type="button" onClick={clearOverride} disabled={saving} style={btnGhost}>
          清除覆寫
        </button>
      </div>

      {lastSave?.success && (
        <div
          style={{
            marginTop: 20,
            padding: 14,
            borderRadius: 10,
            background: '#f0fdf4',
            border: '1px solid #bbf7d0',
            fontSize: 13,
          }}
        >
          <strong>上次儲存（{lastSave.zone_key}）</strong>
          <div style={{ marginTop: 6 }}>對外：{lastSave.displaySummaryLines?.join('、')}</div>
          <div style={{ marginTop: 4, color: '#444' }}>推算：{lastSave.computed?.summaryLines?.join('、')}</div>
        </div>
      )}
    </div>
  );
}

const th = { textAlign: 'left', padding: '8px 6px', borderBottom: '1px solid #ccc', fontSize: 12 };
const td = { padding: '6px', borderBottom: '1px solid #eee', verticalAlign: 'middle' };
const btnSm = {
  padding: '6px 12px',
  fontWeight: 700,
  borderRadius: 8,
  border: '1px solid #ccc',
  background: '#fff',
  cursor: 'pointer',
  fontSize: 13,
};
const btnPrimary = {
  padding: '10px 20px',
  fontWeight: 800,
  borderRadius: 10,
  border: 'none',
  background: '#7b4f2f',
  color: '#fff',
  cursor: 'pointer',
};
const btnGhost = {
  padding: '10px 20px',
  fontWeight: 700,
  borderRadius: 10,
  border: '1px solid #ccc',
  background: '#fff',
  cursor: 'pointer',
};
